<script lang="ts">
	import { goto } from '$app/navigation';
	import { cssInput, getContrastYIQ, glassDesignStyle } from '#lib/helper/color.js';
	import Svg from '#lib/svg/svg.svelte';
	import type { Icon } from '#lib/types/icons.js';
	import type { Snippet } from 'svelte';
	import { fade, slide } from 'svelte/transition';

	interface Props {
		className?: string;
		absolute?: boolean;
		shake?: boolean;
		zoom?: boolean;
		bgColor?: string;
		bgDisabledColor?: string;
		textColor?: string;
		hexColor?: string;
		iconHexColor?: string;
		height?: string;
		width?: string;
		padding?: string;
		showBorder?: boolean;
		href?: string;
		showText?: boolean;
		disabled?: boolean;
		dataTestId?: string;
		title?: string;
		icon?: Icon;
		iconPosition?: 'left' | 'right';
		iconClasses?: string;
		iconSizeClasses?: string;
		disableHoverTextColor?: boolean;
		disableHoverBgColor?: boolean;
		overrideCursor?: boolean;
		fullRounded?: boolean;
		rounded?: string;
		align?: string;
		highlighted?: boolean;
		transitionShowText?: boolean;
		element?: HTMLElement | undefined;
		countBubble?: number;
		bubbleBgColor?: string;
		displayWidget?: boolean;
		bubblePing?: boolean;
		glassDesign?: boolean;
		children?: Snippet;
		slotIcon?: Snippet;
		onclick?: (e?: MouseEvent) => void;
		onmouseenter?: () => void;
		onmouseleave?: () => void;
	}

	let {
		className = '',
		absolute = false,
		shake = false,
		zoom = false,
		bgColor = 'transparent',
		bgDisabledColor = 'disabled',
		textColor = '',
		hexColor = '',
		iconHexColor = '',
		height = '',
		width = '',
		padding = 'p-2',
		showBorder = false,
		href = '',
		showText = true,
		disabled = false,
		dataTestId = '',
		title = '',
		icon = '',
		iconPosition = 'left',
		iconClasses = '',
		iconSizeClasses = 'w-5 h-5',
		disableHoverTextColor = false,
		disableHoverBgColor = false,
		overrideCursor = false,
		fullRounded = false,
		rounded = '',
		align = 'justify-center',
		highlighted = false,
		transitionShowText = false,
		element = $bindable(),
		countBubble = 0,
		bubbleBgColor = 'bg-error text-error-on',
		displayWidget = false,
		bubblePing = false,
		glassDesign = false,
		children,
		slotIcon,
		onclick = undefined,
		onmouseenter = () => {},
		onmouseleave = () => {}
	}: Props = $props();

	function click(e?: MouseEvent) {
		if (disabled) {
			return;
		}

		if (href != '') {
			if (/^(https?:)?\/\//i.test(href)) {
				window.location.href = href;
			} else {
				goto(href);
			}
		}
		onclick?.(e);
	}

	function getBgColor(bgColor: string, disabled: boolean): string {
		if (disabled) {
			return bgDisabledColor;
		}

		return bgColor;
	}

	let cssTextColor: string | undefined = $state();
	let bgColorComputed = $derived(
		displayWidget && bgColor !== 'transparent'
			? 'widget-primary'
			: getBgColor(bgColor, disabled)
	);
	$effect(() => {
		if (glassDesign && bgColor !== 'transparent' && !!bgColor) {
			cssTextColor =
				'brightness-125 ' + (disabled ? ' opacity-50 ' : '') + ' text-' + bgColor + '/20';
			'border ' +
				(disabled
					? 'border-' + bgColor + '/30'
					: 'border-surface/30 dark:border-surface-dark/30 ');
			return;
		}
		if (textColor) {
			cssTextColor =
				'text-' + textColor + ' dark:text-' + textColor.replace('-on', '-dark-on');
		} else if (bgColorComputed && !textColor) {
			cssTextColor = `text-${bgColorComputed}-on dark:text-${bgColorComputed}-dark-on`;
		}
	});
</script>

<div
	bind:this={element}
	class="{fullRounded
		? 'aspect-square flex flex-none items-center justify-center rounded-full overflow-hidden '
		: ''} {height ? height : 'h-full'} flex {width}"
>
	<button
		{onmouseenter}
		{onmouseleave}
		in:fade={{ duration: 200 }}
		class="
		{absolute ? 'absolute' : 'relative'}
        {!glassDesign
			? showBorder && displayWidget
				? 'border border-widget-primary/70 dark:border-widget-primary-dark/30'
				: showBorder
					? 'border border-border dark:border-border-dark'
					: ''
			: ''}
		{glassDesign && showBorder ? 'border border-surface/30 dark:border-surface-dark/70' : ''}
        {fullRounded
			? 'rounded-full aspect-square'
			: rounded
				? rounded
				: className.includes('rounded-')
					? ''
					: 'rounded-md'}
        {highlighted ? 'outline-2 outline-primary outline-offset-1 duration-100' : 'duration-500'}
        {!children || (!showText && !transitionShowText) ? 'aspect-square' : ''}
        transition-all
        flex-none
        {bgColor !== 'transparent'
			? glassDesign
				? `bg-${bgColorComputed}/30 dark:bg-${bgColorComputed}-dark/30`
				: `bg-${bgColorComputed} dark:bg-${bgColorComputed}-dark`
			: ''}
        {bgColor === 'transparent' && !disableHoverBgColor
			? 'hover:bg-secondary/10 hover:dark:bg-secondary-dark/40 ' +
				(disableHoverTextColor
					? ''
					: 'hover:text-secondary dark:hover:text-secondary-on-dark')
			: 'hover:opacity-50'}
        active:bg-secondary
        active:text-secondary-on
        {padding}
        flex items-center {align} gap-1.5
        flex-nowrap text-nowrap
        {cssTextColor}
        {className}
        {height}
        {shake ? 'shake-animation' : ''}
        {zoom ? 'zoom-animation z-10' : ''}
        {overrideCursor ? 'cursor-default' : ''}
		{!onclick ? 'cursor-default' : ''}
        {iconPosition === 'right' ? 'flex-row-reverse' : ''}
		{disabled && displayWidget ? 'opacity-30' : ''}
		{glassDesign && bgColor === 'transparent'
			? cssInput('', {
					...glassDesignStyle,
					rounded:
						fullRounded ||
						rounded === 'rounded-full' ||
						className.includes('rounded-full')
							? 'rounded-full'
							: rounded || (className.includes('rounded-') ? '' : undefined),
					padding: padding ? padding : undefined,
					width: width ? width : undefined
				})
			: ''}
      "
		{disabled}
		class:cursor-not-allowed={disabled}
		data-testid={dataTestId}
		id={dataTestId}
		type="button"
		{title}
		onclickcapture={(e) => {
			e.stopPropagation();
			click(e);
		}}
		style={hexColor
			? 'background-color: ' + hexColor + '; color: ' + getContrastYIQ(hexColor)
			: ''}
	>
		{#if icon}
			<div
				class={iconHexColor ? 'p-1 rounded-full' : ''}
				style={iconHexColor
					? 'background-color: ' +
						iconHexColor +
						'; color: ' +
						getContrastYIQ(iconHexColor)
					: ''}
			>
				<Svg src={icon} className="{iconSizeClasses} shrink-0 {iconClasses}" />
			</div>
		{:else if slotIcon}
			{@render slotIcon()}
		{/if}

		{#if children && showText && !transitionShowText}
			{@render children()}
		{/if}
		{#if children && showText && transitionShowText}
			<div transition:slide={{ duration: 200, axis: 'x' }}>
				{@render children()}
			</div>
		{/if}
		{#if countBubble > 0}
			{#if bubblePing}
				<div
					class="absolute z-10 -top-1.5 -right-1.5 h-4 w-4 rounded-full {bubbleBgColor} animate-ping"
				></div>
			{/if}
			<div
				class="absolute z-10 -top-1.5 -right-1.5 h-4 w-4 flex-none flex items-center justify-center rounded-full text-xs font-semibold {bubbleBgColor}"
			>
				{countBubble}
			</div>
		{/if}
	</button>
</div>

<style>
	@keyframes shake {
		0% {
			transform: translateX(0);
		}
		25% {
			transform: translateX(-5px);
		}
		50% {
			transform: translateX(5px);
		}
		75% {
			transform: translateX(-5px);
		}
		100% {
			transform: translateX(0);
		}
	}

	.shake-animation {
		animation: shake 0.1s linear 5; /* 0.1s * 20 itérations = 2 secondes */
	}

	@keyframes zoom {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		25% {
			transform: scale(1.5);
			opacity: 1;
		}
		50% {
			transform: scale(1);
			opacity: 1;
		}
		75% {
			transform: scale(1.2);
			opacity: 1;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.zoom-animation {
		animation: zoom 0.4s linear 1;
	}
</style>
